import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MovieGrid from "@/components/feature/MovieGrid";
import SectionHeading from "@/components/feature/SectionHeading";
import { filterMovies, sortMovies, movies } from "@/lib/movies";
import { useSeo } from "@/hooks/useSeo";

export default function Search() {
  const [params, setParams] = useSearchParams();
  const query = params.get("q") || "";
  const [keyword, setKeyword] = useState(query);

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  useSeo({
    title: query ? `${query} - 搜索结果 - 91免费看片` : "搜索影视 - 91免费看片",
    description: query
      ? `91免费看片搜索「${query}」的影视结果，为你找到相关的电影、电视剧、综艺与动漫资源。`
      : "在91免费看片搜索你想看的影视内容，覆盖电影、电视剧、综艺、动漫等丰富资源。",
    keywords: query ? `${query},91免费看片,影视搜索` : "91免费看片,影视搜索",
  });

  const results = useMemo(
    () => sortMovies(filterMovies({ keyword: query }), "hot"),
    [query],
  );

  const hotPicks = useMemo(() => sortMovies(movies, "hot").slice(0, 6), []);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (q) setParams({ q });
    else setParams({});
  };

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
      <SectionHeading
        title="影视搜索"
        subtitle="输入影视名称、题材或演员，快速找到想看的内容"
        icon="ri-search-line"
      />

      <form
        onSubmit={submit}
        className="mt-1 flex flex-col gap-3 rounded-xl border border-background-200 bg-background-50 p-3 sm:flex-row sm:items-center"
      >
        <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 py-2">
          <i className="ri-search-line text-base text-foreground-500" />
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="例如：星际迷航、悬疑、古装…"
            className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
          />
        </div>
        <button
          type="submit"
          className="whitespace-nowrap rounded-md bg-primary-600 px-6 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700"
        >
          搜索
        </button>
      </form>

      {query ? (
        <div className="mt-6">
          <p className="text-sm text-foreground-600">
            关键词「<span className="font-semibold text-primary-700">{query}</span>」共找到{" "}
            <span className="font-semibold text-accent-600">{results.length}</span> 条结果
          </p>
          <div className="mt-4">
            <MovieGrid
              movies={results}
              emptyText="暂无搜索结果，请调整关键词"
            />
          </div>
        </div>
      ) : (
        <div className="mt-8">
          <div className="mb-5 flex items-center gap-2 text-sm text-foreground-500">
            <i className="ri-fire-line text-accent-500" />
            还没有输入关键词？先看看大家都在看的热门影视
          </div>
          <MovieGrid movies={hotPicks} />
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/category/电影"
              className="rounded-full border border-background-300 px-4 py-1.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              浏览电影
            </Link>
            <Link
              to="/category/电视剧"
              className="rounded-full border border-background-300 px-4 py-1.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              浏览电视剧
            </Link>
            <Link
              to="/special"
              className="rounded-full border border-background-300 px-4 py-1.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              影视专题
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}